import { Lock, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { useEntitlements } from "@/context/EntitlementContext";

// Presentational metadata for locked features (display only — entitlement decisions
// always come from the backend). Maps a feature key to a label + the lowest plan
// that includes it.
export const FEATURE_META = {
  inquiry_management: { label: "Inquiry Management", plan: "Starter", short: "STARTER" },
  customer_management: { label: "Customer Management", plan: "Starter", short: "STARTER" },
  basic_costing: { label: "Basic Costing", plan: "Starter", short: "STARTER" },
  quotations: { label: "Quotations", plan: "Starter", short: "STARTER" },
  quotation_revisions: { label: "Quotation Revisions", plan: "Starter", short: "STARTER" },
  booking_management: { label: "Booking Management", plan: "Starter", short: "STARTER" },
  basic_payment_tracking: { label: "Basic Payment Tracking", plan: "Starter", short: "STARTER" },
  basic_invoice_receipt: { label: "Basic Invoice & Receipt", plan: "Starter", short: "STARTER" },
  basic_dashboard: { label: "Basic Dashboard", plan: "Starter", short: "STARTER" },
  advanced_costing: { label: "Advanced Costing", plan: "Growth", short: "GROWTH" },
  supplier_cost_management: { label: "Supplier Cost Management", plan: "Growth", short: "GROWTH" },
  advanced_quotation_revision: { label: "Advanced Quotation & Revision", plan: "Growth", short: "GROWTH" },
  payment_schedules: { label: "Payment Schedules", plan: "Growth", short: "GROWTH" },
  deposit_settlement: { label: "Deposit & Settlement Workflow", plan: "Growth", short: "GROWTH" },
  full_invoice_receipt: { label: "Full Invoice & Receipt Workflow", plan: "Growth", short: "GROWTH" },
  operational_reporting: { label: "Operational Reporting", plan: "Growth", short: "GROWTH" },
  trip_operations: { label: "Trip Operations", plan: "Professional", short: "PRO" },
  advanced_booking_operations: { label: "Advanced Booking Operations", plan: "Professional", short: "PRO" },
  advanced_financial_controls: { label: "Advanced Financial Controls", plan: "Professional", short: "PRO" },
  approval_workflows: { label: "Approval Workflows", plan: "Professional", short: "PRO" },
  advanced_reporting: { label: "Advanced Reporting", plan: "Professional", short: "PRO" },
  governance_audit: { label: "Governance & Audit", plan: "Professional", short: "PRO" },
  ai_operational_tasks: { label: "AI-assisted Operational Tasks", plan: "Professional", short: "PRO" },
  advanced_roles_permissions: { label: "Advanced Roles & Permissions", plan: "Professional", short: "PRO" },
  enterprise_roles_permissions: { label: "Enterprise Roles & Permissions", plan: "Enterprise", short: "ENT" },
  advanced_governance: { label: "Advanced Governance", plan: "Enterprise", short: "ENT" },
  advanced_audit_compliance: { label: "Advanced Audit & Compliance", plan: "Enterprise", short: "ENT" },
  organization_reporting: { label: "Organization-wide Reporting", plan: "Enterprise", short: "ENT" },
  advanced_ai_governance: { label: "Advanced AI Governance", plan: "Enterprise", short: "ENT" },
  enterprise_integrations: { label: "Enterprise Integrations", plan: "Enterprise", short: "ENT" },
  priority_support: { label: "Priority Support", plan: "Enterprise", short: "ENT" },
  custom_configuration: { label: "Custom Configuration", plan: "Enterprise", short: "ENT" },
};

const humanize = (k) => (k || "this feature").split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
const planLabel = (planId) => (planId || "").replace("petrinos_", "").replace(/^\w/, (c) => c.toUpperCase()) || "Trial";

export default function FeatureLocked({ featureKey }) {
  const meta = FEATURE_META[featureKey] || { label: humanize(featureKey), plan: "a higher", short: "UPGRADE" };
  const { planId } = useEntitlements();
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm dark:border-ink-line dark:bg-ink-card"
      data-testid={`feature-locked-${featureKey}`}>
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-50 dark:bg-amber-500/10">
        <Lock className="h-5 w-5 text-amber-600 dark:text-amber-400" />
      </div>
      <h2 className="mt-4 font-display text-xl font-bold text-slate-900 dark:text-ink-text" data-testid="locked-feature-name">
        {meta.label}
      </h2>
      <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
        This feature is not included in your current plan
        {planId ? <> (<span className="font-medium text-slate-700 dark:text-ink-text">{planLabel(planId)}</span>)</> : null}.
        It is available from the <span className="font-semibold text-slate-700 dark:text-ink-gold" data-testid="locked-required-plan">{meta.plan}</span> plan.
      </p>
      <button
        onClick={() => toast.info("Please contact your PETRINOS platform administrator to upgrade your plan.")}
        data-testid="upgrade-cta"
        className="mt-6 inline-flex items-center gap-2 rounded-lg bg-ink-gold px-5 py-2.5 text-sm font-semibold text-[#0B1120] hover:brightness-110">
        Upgrade Plan <ArrowUpRight className="h-4 w-4" />
      </button>
      <p className="mt-3 text-xs text-slate-400">Plan upgrades are handled by the platform team. No payment is taken here.</p>
    </div>
  );
}
